// src/context/AuthContext.js
import React, {
  createContext,
  useContext,
  useMemo,
  useState,
  useEffect,
} from 'react';
import axios from 'axios';
import { useToast } from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { syncStaffExclusion } from '../utils/umami';

const AuthContext = createContext();

export const AuthProvider = ({
  children,
  initialIsAuthenticated = false,
  initialUserData = null,
}) => {
  const [isAuthenticated, setIsAuthenticated] = useState(
    initialIsAuthenticated
  );
  const [loading, setLoading] = useState(false);
  const [error] = useState('');
  const [userData, setUserData] = useState(initialUserData);

  const toast = useToast();
  const navigate = useNavigate();

  const setAuthData = (auth, user) => {
    setIsAuthenticated(auth);
    setUserData(user);
  };

  const requestUserData = async ({ withLoading = true } = {}) => {
    if (withLoading) {
      setLoading(true);
    }
    try {
      const response = await axios.get('/api/users/me', {
        withCredentials: true,
      });

      if (response.data?.id) {
        setIsAuthenticated(true);
        setUserData(response.data);
      } else if (response.data?.message === 'Ho bisogno del token') {
        // Special case: API needs token but user is not authenticated
        setIsAuthenticated(false);
        setUserData({ needsToken: true });
      } else {
        // If no valid user data returned, treat as logged out
        setIsAuthenticated(false);
        setUserData(null);
        throw new Error('Sessione non valida');
      }
    } catch (err) {
      console.log(err.message);
      // We can handle re-throw or log here, see note below
    } finally {
      setLoading(false);
    }
  };

  // Login come admin/helper: il dispositivo non entra nelle statistiche
  useEffect(() => {
    syncStaffExclusion(userData);
  }, [userData?.type]);

  // --- POLLING every 30 seconds ---
  useEffect(() => {
    let intervalId;

    if (isAuthenticated) {
      intervalId = setInterval(async () => {
        try {
          const response = await axios.get('/api/users/me', {
            withCredentials: true,
          });

          if (!response.data?.id) {
            if (response.data?.message === 'Ho bisogno del token') {
              setIsAuthenticated(false);
              setUserData({ needsToken: true });
            } else {
              setIsAuthenticated(false);
              setUserData(null);
              clearInterval(intervalId);
              // Show a toast once, then redirect
              toast({
                title: 'La tua sessione è scaduta',
                status: 'warning',
                isClosable: true,
              });
              navigate('/'); // redirect to home
            }
          }
        } catch (err) {
          setIsAuthenticated(false);
          setUserData(null);
          clearInterval(intervalId);
          // Show a toast once, then redirect
          toast({
            title: 'La tua sessione è scaduta',
            status: 'warning',
            isClosable: true,
          });
          navigate('/'); // redirect to home
          // If we get here, `requestUserData` probably threw an error.
          // You can handle the session-expiration logic below or inside requestUserData
        }
      }, 15000);
    }

    return () => clearInterval(intervalId);
  }, [isAuthenticated, navigate, toast]);

  const authValue = useMemo(
    () => ({
      isAuthenticated,
      userData,
      setIsAuthenticated,
      setUserData,
      setAuthData,
      requestUserData,
      loading,
      error,
    }),
    [isAuthenticated, userData, loading, error]
  );

  return (
    <AuthContext.Provider value={authValue}>{children}</AuthContext.Provider>
  );
};

export const useAuth = () => {
  return useContext(AuthContext);
};
